import moment from 'moment';

const intervalDateFormat = 'YYYY-MM-DD';
const timeFormat = 'HH:mm';
const intervalDateTimeFormat = `${intervalDateFormat} ${timeFormat}`;
const childIntervalDateTimeFormats = [
  'DD.MM.YYYY HH:mm:ss',
  'DD.MM.YYYY H:mm:ss',
  'DD.MM.YYYY HH:mm',
  'DD.MM.YYYY H:mm',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD H:mm:ss',
  intervalDateTimeFormat,
  'YYYY-MM-DD H:mm',
  moment.ISO_8601,
];

export const normalizeWorkScheduleAddressDirectory = (cityRegions) => {
  const cityRegionMap = new Map();
  const streetMap = new Map();
  const houseMap = new Map();

  (Array.isArray(cityRegions) ? cityRegions : []).forEach((cityRegion = {}) => {
    const cityRegionId = cityRegion.id ?? cityRegion.cityRegionId;
    const isCityRegion = Number(cityRegionId) > 0;

    if (isCityRegion) {
      cityRegionMap.set(String(cityRegionId), {
        ...cityRegion,
        id: cityRegionId,
        cityRegionId,
        name: cityRegion.name ?? cityRegion.cityRegionName,
      });
    }

    (Array.isArray(cityRegion.streets) ? cityRegion.streets : []).forEach(
      (street = {}) => {
        const streetId = street.id ?? street.streetId;

        if (streetId === undefined || streetId === null) {
          return;
        }

        const streetKey = String(streetId);
        const streetName =
          street.nameWithPrefix ||
          street.name ||
          street.fullName ||
          street.streetName;
        const existingStreet = streetMap.get(streetKey);
        const normalizedStreet = {
          ...(existingStreet || {}),
          ...street,
          id: streetId,
          streetId,
          name: streetName,
          cityRegionIds: [
            ...(existingStreet?.cityRegionIds || []),
            ...(isCityRegion ? [cityRegionId] : []),
          ].filter(
            (id, index, values) =>
              values.findIndex((value) => String(value) === String(id)) ===
              index
          ),
        };

        streetMap.set(streetKey, normalizedStreet);

        (Array.isArray(street.houses) ? street.houses : []).forEach(
          (house = {}) => {
            const houseId = house.id ?? house.houseId;

            if (houseId === undefined || houseId === null) {
              return;
            }

            const existingHouse = houseMap.get(String(houseId));
            const cityRegionIds = [
              ...(existingHouse?.cityRegionIds || []),
              ...(isCityRegion ? [cityRegionId] : []),
            ].filter(
              (id, index, values) =>
                values.findIndex((value) => String(value) === String(id)) ===
                index
            );

            houseMap.set(String(houseId), {
              ...(existingHouse || {}),
              ...house,
              id: houseId,
              houseId,
              streetId,
              streetName,
              cityRegionIds,
              ...(cityRegionIds.length && {
                cityRegionId: cityRegionIds[0],
              }),
            });
          }
        );
      }
    );
  });

  const houses = Array.from(houseMap.values());
  const housesByStreet = houses.reduce((map, house) => {
    const streetKey = String(house.streetId);
    const streetHouses = map.get(streetKey) || [];

    streetHouses.push(house);
    map.set(streetKey, streetHouses);
    return map;
  }, new Map());
  const streets = Array.from(streetMap.values()).map((street) => ({
    ...street,
    houses: housesByStreet.get(String(street.id)) || [],
  }));

  return {
    cityRegions: Array.from(cityRegionMap.values()),
    streets,
    houses,
  };
};

const normalizeParentSchedule = (schedule = {}) => {
  const workScheduleName =
    schedule.workScheduleName || schedule.departmentName || schedule.name || '';

  return {
    ...schedule,
    workScheduleName,
    name: workScheduleName,
    departmentName: workScheduleName,
    children: Array.isArray(schedule.children) ? schedule.children : [],
  };
};

export const normalizeParentSchedules = (schedules) =>
  Array.isArray(schedules) ? schedules.map(normalizeParentSchedule) : [];

const getScheduleMonthText = ({ scheduleMonth, scheduleYear }) => {
  const monthNumber = Number(scheduleMonth);
  const yearNumber = Number(scheduleYear);

  if (!Number.isFinite(monthNumber) || !Number.isFinite(yearNumber)) {
    return '';
  }

  const normalizedMonth = Math.min(Math.max(monthNumber, 1), 12);

  return `${String(normalizedMonth).padStart(2, '0')}.${yearNumber}`;
};

const getWorkerWorkingHours = (intervalsByDate = {}) =>
  Number(
    Object.values(intervalsByDate)
      .reduce((totalHours, interval = {}) => {
        const from = moment(interval.from, timeFormat, true);
        const to = moment(interval.to, timeFormat, true);

        if (!from.isValid() || !to.isValid()) {
          return totalHours;
        }

        if (to.isSameOrBefore(from)) {
          to.add(1, 'day');
        }

        return totalHours + to.diff(from, 'minutes') / 60;
      }, 0)
      .toFixed(2)
  );

const normalizeChildScheduleService = (service = {}) => ({
  ...service,
  id: service.workScheduleServiceNameId,
  name: service.workScheduleServiceName,
  comment: service.workScheduleServiceNameComment,
  services: (service.requestTypesParentTypeId || []).map((serviceId) => ({
    serviceId,
    reasonIds: service.requestTypesTypeId || [],
  })),
});

const normalizeChildScheduleIntervals = (intervals = []) =>
  intervals.reduce((acc, interval = {}) => {
    const fromDate = moment(
      interval.fromDate,
      childIntervalDateTimeFormats,
      true
    );
    const toDate = moment(interval.toDate, childIntervalDateTimeFormats, true);

    if (!fromDate.isValid() || !toDate.isValid()) {
      return acc;
    }

    acc[fromDate.format(intervalDateFormat)] = {
      from: fromDate.format(timeFormat),
      to: toDate.format(timeFormat),
    };

    return acc;
  }, {});

const normalizeChildScheduleWorkers = (workers = []) => {
  const workIntervals = {};
  const performers = (Array.isArray(workers) ? workers : [])
    .map((worker) => {
      const intervalsByDate = normalizeChildScheduleIntervals(
        worker.interval || []
      );

      if (worker.id !== undefined && worker.id !== null) {
        workIntervals[String(worker.id)] = intervalsByDate;
      }

      return {
        ...worker,
        id: worker.id,
        fullName: worker.fio || worker.fullName || '',
        phoneNumber: worker.phoneNumber || '',
        speciality: worker.specialityName || worker.speciality || '',
        specialities: [worker.specialityName || worker.speciality].filter(
          Boolean
        ),
        specializations: [worker.specialityName || worker.speciality].filter(
          Boolean
        ),
        workingHours: getWorkerWorkingHours(intervalsByDate),
      };
    })
    .filter((worker) => worker.id !== undefined && worker.id !== null);

  return { performers, workIntervals };
};

const normalizeChildScheduleAddresses = (addressData = {}) => {
  const city = addressData.workScheduleAddresses;
  const cityRegionIds = [
    ...(Array.isArray(addressData.cityRegionIds)
      ? addressData.cityRegionIds
      : []),
    ...(Array.isArray(city?.cityRegionIds) ? city.cityRegionIds : []),
    addressData.cityRegionId ?? city?.cityRegionId,
  ]
    .map(Number)
    .filter(
      (id, index, values) =>
        Number.isFinite(id) &&
        id > 0 &&
        values.findIndex((value) => value === id) === index
    );
  const cityRegionId = cityRegionIds[0];
  const cityRegionName = addressData.cityRegionName ?? city?.cityRegionName;
  const cityRegions = cityRegionIds.map((id) => ({
    id,
    ...(cityRegionIds.length === 1 && cityRegionName
      ? { name: cityRegionName }
      : {}),
  }));
  const cities =
    Number(city?.cityId) > 0
      ? [{ id: city.cityId, name: city.cityName, cityId: city.cityId }]
      : [];
  const streets = (Array.isArray(city?.streets) ? city.streets : [])
    .filter((street) => Number(street.streetId) > 0)
    .map((street) => ({
      ...street,
      id: street.streetId,
      name: street.streetName,
      cityId: city.cityId,
    }));
  const houses = streets.flatMap((street) =>
    (Array.isArray(street.houses) ? street.houses : [])
      .filter((house) => Number(house.houseId) > 0)
      .map((house) => {
        const name = [
          house.name || house.houseName,
          house.corps && `корп. ${house.corps}`,
        ]
          .filter(Boolean)
          .join(' ');

        return {
          ...house,
          id: house.houseId,
          name,
          addressName: name,
          cityId: city.cityId,
          streetId: street.streetId,
          streetName: street.streetName,
        };
      })
  );

  return {
    companyId: addressData.serviceCompanyId,
    allCompanyAddresses: Boolean(
      Number(addressData.serviceCompanyId) > 0 &&
        !cityRegions.length &&
        !cities.length &&
        !streets.length &&
        !houses.length
    ),
    cityIds: cities.map((item) => item.id),
    cityRegionId,
    cityRegionIds,
    streetIds: streets.map((item) => item.id),
    houseIds: houses.map((item) => item.id),
    cities,
    cityRegions,
    streets,
    houses,
  };
};

const normalizeChildSchedule = (schedule = {}) => {
  const service = normalizeChildScheduleService(
    schedule.workScheduleServices || {}
  );
  const address = normalizeChildScheduleAddresses(
    schedule.workScheduleAddresses || {}
  );
  const monthText = getScheduleMonthText(schedule);
  const { performers, workIntervals } = normalizeChildScheduleWorkers(
    schedule.workScheduleWorker
  );

  return {
    ...schedule,
    id: schedule.periodId,
    periodId: schedule.periodId,
    parentId: schedule.workScheduleId,
    workScheduleId: schedule.workScheduleId,
    departmentName: service.name || 'График по периоду',
    startMonth: monthText,
    endMonth: monthText,
    period: {
      startMonth: monthText,
      endMonth: monthText,
    },
    ...address,
    address,
    serviceSetIds:
      service.id !== undefined && service.id !== null ? [service.id] : [],
    serviceSets:
      service.id !== undefined && service.id !== null ? [service] : [],
    workScheduleServiceNameId: service.id,
    workers: performers,
    performers,
    quantityOfWorkers: performers.length,
    workIntervals,
  };
};

export const normalizeChildSchedules = (schedules) =>
  Array.isArray(schedules) ? schedules.map(normalizeChildSchedule) : [];

const normalizeServiceName = (serviceName = {}) => ({
  ...serviceName,
  id: serviceName.id,
  name: serviceName.name,
  comment: serviceName.comment,
  services: Array.isArray(serviceName.services) ? serviceName.services : [],
});

export const normalizeServiceNames = (serviceNames) =>
  Array.isArray(serviceNames) ? serviceNames.map(normalizeServiceName) : [];

export const normalizeServiceDetails = (serviceDetails) => {
  if (!Array.isArray(serviceDetails)) {
    return [];
  }

  const servicesById = new Map();

  serviceDetails.forEach((item = {}) => {
    const serviceId = item.parentTypeId;

    if (serviceId === undefined || serviceId === null) {
      return;
    }

    const serviceKey = String(serviceId);

    if (!servicesById.has(serviceKey)) {
      servicesById.set(serviceKey, {
        id: serviceId,
        serviceId,
        name: item.parentTypeName,
        serviceName: item.parentTypeName,
        reasonIds: [],
        reasonNames: [],
        reasons: [],
      });
    }

    const service = servicesById.get(serviceKey);
    const reasonKey = String(item.typeId);

    if (item.typeId === undefined || item.typeId === null) {
      return;
    }

    if (service.reasonIds.map(String).includes(reasonKey)) {
      return;
    }

    service.reasonIds.push(item.typeId);
    service.reasonNames.push(item.typeName);
    service.reasons.push({
      id: item.typeId,
      reasonId: item.typeId,
      name: item.typeName,
    });
  });

  return [...servicesById.values()];
};

export const normalizeServicesWithTree = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  const servicesById = new Map();

  items.forEach((item = {}) => {
    if (item.parentId === undefined || item.parentId === null) {
      return;
    }

    const serviceKey = String(item.parentId);

    if (!servicesById.has(serviceKey)) {
      servicesById.set(serviceKey, {
        id: item.parentId,
        serviceId: item.parentId,
        name: item.parentName,
        serviceName: item.parentName,
        isSelected: false,
        isImmediate: item.isImmediate,
        reasons: [],
      });
    }

    const service = servicesById.get(serviceKey);
    service.isSelected = service.isSelected || item.parentIsSelected === true;
    service.isImmediate =
      service.isImmediate === true || item.isImmediate === true;

    const serviceTypes = Array.isArray(item.serviceTypes)
      ? item.serviceTypes
      : [];

    serviceTypes.forEach((type = {}) => {
      if (type.typeId === undefined || type.typeId === null) {
        return;
      }

      const existingReason = service.reasons.find(
        (reason) => String(reason.id) === String(type.typeId)
      );

      if (existingReason) {
        existingReason.isSelected =
          existingReason.isSelected || type.typeIsSelected === true;
        existingReason.isImmediate =
          existingReason.isImmediate === true || type.isImmediate === true;
        return;
      }

      service.reasons.push({
        id: type.typeId,
        reasonId: type.typeId,
        name: type.typeName,
        isSelected: type.typeIsSelected === true,
        isImmediate: type.isImmediate,
        serviceTypes: Array.isArray(type.serviceTypes) ? type.serviceTypes : [],
      });
    });
  });

  return [...servicesById.values()];
};
